import { holdInUtc, holdPropsInUtc, PushNotification } from "@repo/helpers";
import { KeyedArray } from "@repo/models";
import { useCallback } from "react";

import { useSettingsQuery } from "../helpers/queryHooks";
import { RepOnboardingAudit } from "../models";
import * as Queries from "./queries";
import { useQueryWrapper, useRepPortalApi } from "./repPortalApi";

export function useRepOnboardingAuditApi() {
	const { Get, Post } = useRepPortalApi();
	const [settings] = useSettingsQuery();
	const timesInLocal = settings?.timesInLocal;

	const saveRepOnboardingAudit = useCallback(
		function (audit: RepOnboardingAudit) {
			return Post<number>("SaveOnboardingAudit", null, audit);
		},
		[Post],
	);

	const initialize = useCallback(
		function (audit: RepOnboardingAudit) {
			holdPropsInUtc(audit, "auditDate", "exceptionDate", "onboardingDate", "dateOfFirstSale");

			audit.backgroundCheck.completionDate = holdInUtc(audit.backgroundCheck.completionDate);
			audit.backgroundCheck.dateRequested = holdInUtc(audit.backgroundCheck.dateRequested);
			audit.backgroundCheck.signatureDate = holdInUtc(audit.backgroundCheck.signatureDate);

			audit.trainingExam.examDate = holdInUtc(audit.trainingExam.examDate);
			audit.trainingExam.trainingDate = holdInUtc(audit.trainingExam.trainingDate);

			audit.drugTest.collectionDate = holdInUtc(audit.drugTest.collectionDate);
			audit.drugTest.signatureDate = holdInUtc(audit.drugTest.signatureDate);

			audit.performanceAndStandards.signatureDate = holdInUtc(audit.performanceAndStandards.signatureDate);

			audit.nrgConsentForm.signatureDate = holdInUtc(audit.nrgConsentForm.signatureDate);

			return audit;
		},
		[timesInLocal],
	);

	const loadRepOnboardingAudit = useCallback(
		function (repOnboardingAuditId: number) {
			return Get<RepOnboardingAudit>("GetOnboardingAudit", {
				repOnboardingAuditId,
			}).then(initialize);
		},
		[Get, initialize],
	);

	const getRepOnboardingAudits = useCallback(
		async function (repId: number) {
			const onboardingAudits = await Get<RepOnboardingAudit[]>("OnboardingAudits", { repId });
			return onboardingAudits.map(initialize);
		},
		[Get, initialize],
	);

	const loadRepOnboardingAudits = useCallback(
		function (repId: number) {
			return async function () {
				if (!repId) return defaultArr();
				try {
					const audits = await getRepOnboardingAudits(repId);
					return new KeyedArray<RepOnboardingAudit>(audits, (b) => b.representativeOnboardingAuditId);
				} catch (e) {
					PushNotification("Unable to retrieve onboarding audits.", "error");
					return defaultArr();
				}
			};
		},
		[getRepOnboardingAudits],
	);

	const useRepOnboardingAuditsQuery = (repId: number) =>
		useQueryWrapper(Queries.ONBOARDING_AUDITS, [repId, loadRepOnboardingAudits], loadRepOnboardingAudits(repId));

	return {
		saveRepOnboardingAudit,
		loadRepOnboardingAudit,
		getRepOnboardingAudits,
		useRepOnboardingAuditsQuery,
	};
}

const defaultArr = () => new KeyedArray<RepOnboardingAudit>([], (b) => b.representativeOnboardingAuditId);
